import { useState, useCallback } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import { PictureAsPdf, Print } from '@mui/icons-material';
import { pdf } from '@react-pdf/renderer';
import { useAuth } from '../../auth/AuthProvider';

const IconOrderPDF = ({ order }) => {
    const [isPrinting, setIsPrinting] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const { company } = useAuth();
    const resolveOrderCode = useCallback((orderData) => (
        orderData?.public_code || orderData?.publicCode || orderData?.supplier_code || orderData?.supplierCode || orderData?.number || orderData?.id
    ), []);

    const generatePDF = useCallback(async () => {
        if (!order) return null;
        const { default: OrderPDF } = await import('./OrderPDF');
        return <OrderPDF order={order} company={company} />;
    }, [order, company]);

    const handlePrint = async () => {
        if (!order || isPrinting) return;
        
        setIsPrinting(true);
        try {
            const pdfDoc = await generatePDF();
            const blob = await pdf(pdfDoc).toBlob();
            const url = URL.createObjectURL(blob);
            
            // Solución todo-en-uno sin nueva pestaña
            const iframe = document.createElement('iframe');
            iframe.style.position = 'fixed';
            iframe.style.right = '0';
            iframe.style.bottom = '0';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = 'none';
            iframe.src = url;
            document.body.appendChild(iframe);
            
            iframe.onload = () => {
                setTimeout(() => {
                    iframe.contentWindow?.print();
                    // Limpieza después de imprimir
                    setTimeout(() => {
                        document.body.removeChild(iframe);
                        URL.revokeObjectURL(url);
                    }, 10000); // 10 segundos de margen
                    setIsPrinting(false);
                }, 500); // Pequeño delay para asegurar carga
            };
        } catch (error) {
            console.error("Error al imprimir:", error);
            setIsPrinting(false);
        }
    };

    const handleDownload = async () => {
        if (!order || isDownloading) return;
        
        setIsDownloading(true);
        try {
            const pdfDoc = await generatePDF();
            const blob = await pdf(pdfDoc).toBlob();
            const url = URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.href = url;
            const code = resolveOrderCode(order);
            const safeCode = code ? String(code).replace(/[^a-z0-9-_]/gi, '') : Date.now();
            link.download = `orden_${safeCode}.pdf`;
            document.body.appendChild(link);
            link.click();
            
            // Limpieza diferida
            setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                setIsDownloading(false);
            }, 200);
        } catch (error) {
            console.error("Error al descargar:", error);
            setIsDownloading(false);
        }
    };

    return (
        <>
            <Tooltip title={order ? "Imprimir orden" : "Seleccione una orden"}>
                <span>
                    <IconButton 
                        color="primary"
                        onClick={handlePrint}
                        disabled={!order || isPrinting}
                        sx={{ 
                            border: '1px solid',
                            borderColor: order ? 'primary.main' : 'text.disabled',
                            marginRight: '5px',
                            '&:hover': order ? { backgroundColor: 'action.hover' } : {}
                        }}
                    >
                        {isPrinting ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            <Print fontSize="small" />
                        )}
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title={order ? "Descargar PDF" : "Seleccione una orden"}>
                <span>
                    <IconButton 
                        color="primary"
                        onClick={handleDownload}
                        disabled={!order || isDownloading}
                        sx={{ 
                            border: '1px solid',
                            borderColor: order ? 'primary.main' : 'text.disabled',
                            '&:hover': order ? { backgroundColor: 'action.hover' } : {}
                        }}
                    >
                        {isDownloading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            <PictureAsPdf fontSize="small" />
                        )}
                    </IconButton>
                </span>
            </Tooltip>
        </>
    );
};

export default IconOrderPDF;
